import
  React, {
  useContext
} from 'react';

import
  styled
from 'styled-components';

import {
  useHistory,
  useLocation
} from 'react-router-dom';

import {
  ColorSvg,
  HContainer,
  Image,
  JourneyProvider,
  Text,
  VContainerH,
  VContainerVH,
  VFillContainer
} from '@vodacom/web-common';

import {
  Environment
} from '@vodacom/web-env';

import {
  Sentry
} from '@vodacom/web-sentry-react';

import {
  CustomColors,
  FeatureToggles,
  Icons
} from '../../../constants';

import {
  PrimaryButton,
  SecondaryButton
} from 'components';

import {
  withScreenViewTagging
} from 'hocs';

import {
  CommonProvider
} from 'providers';

import {
  getStorageWithExpiry,
  isFeatureEnabled,
  isValidEncMsisdn,
  setStorageWithExpiry
} from 'helpers';

import {
  AppContext
} from 'context';

const MsisdnInput = styled.input`
  margin: 10px 0;
  font-size: 16px;
  padding: 7px;
`;

const CONTENT_TEXT = `
connectU is a FREE service you can enjoy by connecting to the Vodacom network.
Please switch from Wi-Fi to mobile data and refresh the page to access connectU.

Should you still see this page, please try again later.
`;

const OnboardingSplashUI = () => {

  const { config } = useContext(AppContext);

  const [
    state,
    setState
  ] = React.useState({
    encryptedMsisdn: '',
    ignoreSplashRedirect: sessionStorage.getItem('JourneyProvider_ignoreSplashRedirect') === 'true'
  });

  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location?.search);
  const encMsisdn = urlParams.get('encMsisdn');
  const acceptedLanding = JourneyProvider.get('Common', 'acceptedLanding', false);

  const getMsisdnDetails = async (encMsisdn) => {

    const res = await CommonProvider.getMsisdnDetails(encMsisdn);

    if (!res?.ok || !res?.result) {
      return;
    }

    res?.result?.id && setStorageWithExpiry('msisdn', 'hashedMsisdn', res?.result?.id, false, 30 * 24 * 3600 * 1000);
    res?.result?.type && setStorageWithExpiry('msisdn', 'msisdnType', res?.result?.type, false, 30 * 24 * 3600 * 1000);
  };

  React.useEffect(() => {

    if (state.ignoreSplashRedirect) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      ignoreSplashRedirect: urlParams.get('splashRedirect') === 'false'
    }));

    sessionStorage.setItem('JourneyProvider_ignoreSplashRedirect', urlParams.get('splashRedirect') === 'false');
  }, []);

  React.useEffect(() => {

    if (
      state.ignoreSplashRedirect &&
      isValidEncMsisdn(getStorageWithExpiry('msisdn', 'encMsisdn', '')) &&
      !isValidEncMsisdn(encMsisdn)
    ) {

      return;
    }

    if (isValidEncMsisdn(encMsisdn)) {

      getMsisdnDetails(encMsisdn);
      setStorageWithExpiry('msisdn', 'encMsisdn', encMsisdn, false, 30 * 24 * 3600 * 1000);
      history.push(acceptedLanding ? '/home' : '/landing');
      return;
    }

    if (isValidEncMsisdn(getStorageWithExpiry('msisdn', 'encMsisdn', ''))) {

      history.push(acceptedLanding ? '/home' : '/landing');
      return;
    }

    const initHeaderEnrichment = () => {

      if (!isValidEncMsisdn(encMsisdn) && encMsisdn !== 'voda-hotspot' && !getStorageWithExpiry('Common', 'enrichmentRedirected', '')) {

        setStorageWithExpiry('Common', 'enrichmentRedirected', true);
        window.location = `${Environment.current.headerEnrichmentUrl}?origin=${Environment.current.uiHost}`;
        return;
      }
    };

    const initConfig = async () => {

      if (!isFeatureEnabled(config, FeatureToggles.EnableHeaderEnrichment)) {

        history.push('/landing');
        return;
      }

      initHeaderEnrichment();

      if (!encMsisdn || isValidEncMsisdn(encMsisdn) || Environment.current.name === 'Local') {
        return;
      }

      if (!isFeatureEnabled(config, FeatureToggles.EnableSentry)) {
        return;
      }

      Sentry.error({
        journey: 'Onboarding',
        component: 'SplashPage',
        error: Error('Header enrichment failed')
      });
    };

    initConfig();

  }, [ history, location ]);

  return (

    <VContainerH
      width={'100%'}
      height={'100vh'}
      boxSizing={'border-box'}
      margin={'0'}
      padding={'106px 24px 32px'}>

      <Image
        src={Icons.ConnectU}
        width={'164px'}
        height={'40px'}/>

      <HContainer
        justifyContent={'space-around'}
        width={'70%'}
        margin={'20px 0'}>

        <ColorSvg
          src={Icons.Wifi}
          color={CustomColors.Grey11}
          width={'40px'}
          height={'40px'}/>

        <ColorSvg
          src={Icons.LongArrow}
          color={CustomColors.Grey11}
          width={'40px'}
          height={'40px'}/>

        <ColorSvg
          src={Icons.UpdownArrows}
          color={CustomColors.Grey11}
          width={'38px'}
          height={'38px'}/>

      </HContainer>

      <Text
        padding={'0 0 10px 0'}
        color={CustomColors.Black}
        fontSize={'20px'}
        fontWeight={'bold'}>

        {'Enable mobile data'}
      </Text>

      <Text
        color={CustomColors.Grey7}
        fontSize={'16px'}
        textAlign={'center'}
        whiteSpace={'pre-wrap'}>

        {CONTENT_TEXT}
      </Text>

      { (Environment.current?.env === 'dev' || Environment.current?.env === 'qa') &&

        <VContainerVH
          margin={'auto'}>

          <Text
            color={CustomColors.Grey7}
            fontSize={'16px'}
            textAlign={'center'}>

            {'Insert the encrypted msisdn in the text box.'}
          </Text>

          <MsisdnInput
            value={state.encryptedMsisdn}
            onChange={e => {

              if (!e || !e.target || typeof e.target.value !== 'string') {
                return;
              }

              setState(prevState => ({
                ...prevState,
                encryptedMsisdn: e.target.value
              }));
            }}/>

          <SecondaryButton
            text={'Continue Journey'}
            width={'150px'}
            onClick={async () => {

              getMsisdnDetails(state.encryptedMsisdn);

              setStorageWithExpiry('msisdn', 'encMsisdn', state.encryptedMsisdn, false, 30 * 24 * 3600 * 1000);
              history.push('/landing');
            }}/>

        </VContainerVH>
        }

        <VFillContainer
          width={'100%'}
          alignItems={'center'}
          justifyContent={'flex-end'}>

        { state.ignoreSplashRedirect && (isValidEncMsisdn(encMsisdn) || isValidEncMsisdn(getStorageWithExpiry('msisdn', 'encMsisdn', '')))
            ?
            <PrimaryButton
              text={'Go to homepage'}
              flexDirection={'flex-end'}
              onClick={() => {

                if (!acceptedLanding) {

                  history.push('/landing');

                  return;
                }

                history.push('/home');
              }}/>
            :
            <PrimaryButton
              text={'Refresh'}
              flexDirection={'flex-end'}
              onClick={() => {

                window.location = `${Environment.current.headerEnrichmentUrl}?origin=${Environment.current.uiHost}`;
                return;
              }}/>
        }
        </VFillContainer>

    </VContainerH>
  );
};

OnboardingSplashUI.pageName = 'cbu: connectu: splash screen';
OnboardingSplashUI.tagViewData = {
  page_section: 'onboarding: splash screen'
};

export const OnboardingSplash = withScreenViewTagging(OnboardingSplashUI);